import { ApiClient, CreationStatus, FileResponse } from '../../api/client';
import { VideoPlayer } from '../creation/VideoPlayer';
import { ProcessingState } from '../creation/ProcessingState';
import { FailedState } from '../creation/FailedState';
import { PaymentRequired } from '../creation/PaymentRequired';
import { DraftCreationHandler } from '../creation/DraftCreationHandler';
import { SendToTelegramButton } from '../common/SendToTelegramButton';
import { OwnerInfo } from '../creation/OwnerInfo';
import { debugLog } from '../../utils/logger';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

export interface StrategyContentProps {
  creation: Awaited<ReturnType<typeof ApiClient.getCreation>>;
  userInfo: UserInfo;
  inputVideo?: FileResponse | undefined;
  outputVideo: FileResponse | undefined;
  onDownload: (url: string, filename: string) => void;
  onPaymentComplete: (newBalance: number) => void;
  creationId: string;
}

/**
 * KovrificationStrategy component
 * 
 * This component handles the UI for the kovrification strategy (known as "C-roll" in the frontend).
 * It displays different content based on the creation status.
 */
export function KovrificationStrategy({
  creation,
  userInfo,
  outputVideo,
  onPaymentComplete,
  creationId
}: StrategyContentProps) {
  const { t } = useTranslation();

  // Function to handle direct download
  const handleDirectDownload = (url: string, filename: string) => {
    posthog.capture('download_creation_clicked', { 
      creation_id: creationId, 
      filename: filename,
      strategy_type: 'kovrification'
    });
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.setAttribute('target', '_blank'); // Fallback for mobile
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Wrapping all states in a div with padding for consistency
  return (
    <div className="p-4">
      {(() => {
        // Handle draft status with the dedicated component
        if (creation.status === CreationStatus.DRAFT) {
          return (
            <DraftCreationHandler
              creation={creation}
              userInfo={userInfo}
              creationId={creationId}
              strategyType="kovrification"
              onUploadComplete={() => {
                window.location.reload();
              }}
              onPaymentComplete={onPaymentComplete}
            />
          );
        }

        if (creation.status === CreationStatus.FAILED) {
          return <FailedState onRetry={() => window.location.reload()} />;
        }

        if (creation.status === CreationStatus.WAITING_FOR_PAYMENT) {
          return (
            <PaymentRequired
              creationId={creationId}
              processingPrice={creation.strategy.price}
              currentBalance={userInfo.balance}
              onPaymentComplete={onPaymentComplete}
              strategyType="kovrification"
            />
          );
        }

        if (creation.status === CreationStatus.COMPLETED && outputVideo) {
          const videoFilename = `${creation.name || t('kovrificationStrategy.defaultFilenamePart', 'video')}_${creationId.substring(0, 8)}.mp4`;

          return (
            <div className="space-y-6">
              {/* Header Section Card */}
              <div 
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 animate-fadeInUp"
                style={{ animationDelay: '0.1s' }}
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {creation.name || t('kovrificationStrategy.defaultTitle', 'Your Video Creation')}
                </h2>
                <OwnerInfo owner={creation.owner} isShared={creation.is_shared} />
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {t('kovrificationStrategy.description', 'This is your C-Roll video, processed and ready for you.')}
                </p>
              </div>

              {/* Video Player Card */}
              <div 
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-fadeInUp"
                style={{ animationDelay: '0.2s' }}
              >
                <div className="bg-black">
                  <VideoPlayer
                    title=""
                    videoUrl={outputVideo.url}
                    showDownloadButton={false}
                  />
                </div>
              </div>

              {/* Action Buttons Card/Section */}
              <div 
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 animate-fadeInUp"
                style={{ animationDelay: '0.3s' }}
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  {t('kovrificationStrategy.actionsTitle', 'Actions')}
                </h3>
                <div className="flex flex-col space-y-3">
                  <button 
                    onClick={() => handleDirectDownload(outputVideo.url, videoFilename)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white font-medium rounded-lg transition-all duration-200 ease-in-out hover:bg-blue-600 hover:shadow-md hover:-translate-y-0.5 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 w-full"
                    title={t('videoPlayer.downloadButtonTitle', 'Download Video')}
                  >
                    <span className="material-symbols-outlined">download</span>
                    <span>{t('videoPlayer.downloadButtonText', 'Download')}</span>
                  </button>
                  {/* Assuming SendToTelegramButton is a component that might not directly accept all Tailwind classes for complex hover states,
                      or we want to provide a consistent wrapper for hover effects if the button itself is more basic.
                      If SendToTelegramButton internally handles its hover/focus well, this wrapper might be simplified or removed.
                      For now, applying hover to its container if it makes sense, or ideally SendToTelegramButton would have a className prop.
                  */}
                  <div className="w-full transition-all duration-200 ease-in-out hover:opacity-90 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 dark:focus-within:ring-offset-gray-800 rounded-lg">
                    <SendToTelegramButton
                      creationId={creationId}
                      resultFileId={outputVideo.id}
                      momentTitle={creation.name || t('kovrificationStrategy.defaultTitle', 'Your Video Creation')}
                      isShared={creation.is_shared}
                      strategyType="kovrification"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (creation.status === CreationStatus.IN_PROGRESS || creation.status === CreationStatus.PENDING) {
          return <ProcessingState 
            creation={creation} 
            strategyType="kovrification" 
            onStatusChange={(updatedCreation) => {
              debugLog('Processing completed:', updatedCreation.status);
            }}
          />;
        }

        return null; // Should not happen if statuses are handled
      })()}
    </div>
  );
}